import { useEffect, useState } from "react";
import { Check, Search } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../ui/input";

// Picks a gym member or staff user by searching on the server: GET /members and GET /staff return one page (20) at a
// time, so a list loaded up front only ever offered the first 20.

const SEARCH_DELAY_MS = 300;
const RESULT_LIMIT = 20;
export const MIN_QUERY_LENGTH = 3;

// Text made only of digits, +, spaces, dashes or brackets is a phone number; returns its digits, otherwise "".
function phoneDigits(query) {
  return /^[\d\s+()-]+$/.test(query) ? query.replace(/\D/g, "") : "";
}

export function memberSearchParams(query) {
  const q = query.trim();
  const digits = phoneDigits(q);
  return digits ? { phone: digits, limit: RESULT_LIMIT } : { name: q, limit: RESULT_LIMIT };
}

// GET /staff matches `name` against the name, email and phone.
export function staffSearchParams(query) {
  const q = query.trim();
  return { name: phoneDigits(q) || q, limit: RESULT_LIMIT };
}

const KINDS = {
  member: {
    search: async (query) => (await api.get("/members", { params: memberSearchParams(query) })).data?.members || [],
    details: (m) => `${m.phone || ""} ${m.memberId ? `#${m.memberId}` : ""}`,
    noun: "members",
  },
  staff: {
    search: async (query) => (await api.get("/staff", { params: staffSearchParams(query) })).data?.staff || [],
    details: (s) => `${s.role || "Staff"} ${s.phone ? `· ${s.phone}` : ""}`,
    noun: "staff",
  },
};

export function PersonSearchPicker({ kind, value, onChange, initialQuery = "" }) {
  const { search, details, noun } = KINDS[kind];
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const tooShort = query.trim().length < MIN_QUERY_LENGTH;

  useEffect(() => {
    setResults([]);
    setFailed(false);
    if (tooShort) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const found = await search(query);
        if (!cancelled) setResults(found);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, tooShort, search]);

  let status = null;
  if (tooShort) status = `Type at least ${MIN_QUERY_LENGTH} letters or digits to search`;
  else if (loading) status = "Searching…";
  else if (failed) status = "Search failed. Try again.";
  else if (results.length === 0) status = `No ${noun} found`;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or phone..."
          aria-label={`Search ${noun}`}
          className="h-9 pl-8 text-xs rounded-sm bg-background border-border"
          autoFocus
        />
      </div>
      <div role="listbox" aria-label={noun} className="max-h-60 overflow-y-auto rounded-sm border border-border divide-y divide-border">
        {status ? (
          <p className="px-3 py-3 text-xs text-muted-foreground text-center">{status}</p>
        ) : (
          results.map((person) => {
            const selected = person.id === value;
            return (
              <button
                key={person.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => onChange(person.id)}
                className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-2 hover:bg-secondary/40 ${selected ? "bg-primary/10" : ""}`}
              >
                <span className="min-w-0">
                  <span className="font-medium text-foreground">{person.name}</span>{" "}
                  <span className="text-muted-foreground">{details(person)}</span>
                </span>
                {selected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
